import { inject, Injectable, Injector } from '@angular/core';
import { ENV_TOKEN } from '@clean-code/models/common';
import { TenantService } from '@clean-code/shared/util/util-tenant';
import { IWidgetTypeProvider } from '../models/widget-type.models';
import { IWidgetTypeConfigComponent } from '../models/widget-types-config.models';
import { IWidgetTypeContentComponent } from '../models/widget-types-content.models';

@Injectable({
  providedIn: 'root',
})
export class WidgetTypeProvider {
  private tenantService = inject(TenantService);
  private currentTenant = inject(ENV_TOKEN).tenant;

  private widgetTypes = new Map<string, IWidgetTypeProvider>();

  public add(
    widgetTypeName: string,
    configClass: { new (injector?: Injector): IWidgetTypeConfigComponent },
    contentClass: { new (injector?: Injector): IWidgetTypeContentComponent },
    tenants: string[],
    minItemRows = 7,
    minItemCols = 5,
    permissions: string[] = [],
  ): void {
    // Register immediately to avoid startup race conditions; validate tenants in background.
    if (!this.getProvider(widgetTypeName)) {
      this.widgetTypes.set(widgetTypeName, {
        widgetTypeName,
        configCmp: configClass,
        contentCmp: contentClass,
        minItemRows,
        minItemCols,
        tenant: tenants,
        permissions,
      });
    }

    void Promise.all(
      tenants.map((t) => this.tenantService.validateTenant(t)),
    ).catch((err: unknown) => {
      // Avoid unhandled promise rejections when callers don't await registration.
      console.error(err);
    });
  }

  public getProvider(widgetTypeName: string): IWidgetTypeProvider | undefined {
    return this.widgetTypes.get(widgetTypeName);
  }

  /**
   * A widget type is available when it is registered in this app's bundle and
   * declares the app's tenant. Everything else must not be rendered ( i.e. the code
   * and/or the backend query simply does not exist here).
   */
  public isAvailable(widgetTypeName: string): boolean {
    const provider = this.getProvider(widgetTypeName);

    if (!provider) {
      return false;
    }

    if (!provider.tenant?.length || !this.currentTenant) {
      return true;
    }

    return provider.tenant.includes(this.currentTenant);
  }

  public getConfigComponent(
    widgetTypeName: string,
  ): { new (injector?: Injector): IWidgetTypeConfigComponent } | null {
    return this.getProvider(widgetTypeName)?.configCmp ?? null;
  }

  public getContentComponent(
    widgetTypeName: string,
  ): { new (injector?: Injector): IWidgetTypeContentComponent } | null {
    return this.getProvider(widgetTypeName)?.contentCmp ?? null;
  }

  public getAll(): IWidgetTypeProvider[] {
    return Array.from(this.widgetTypes.values());
  }
}
